/**
 * Split an email header address into a display name and a bare address.
 *
 * `from` / `to` arrive as either `"Display Name <addr@host>"` or a bare
 * `"addr@host"`. The row shows the name where there is one and the address
 * beside it, muted — so the two have to be separated before render.
 *
 * It lives here because the inbox row and the client console's Emails panel
 * both need it, and they already share one CSS recipe. A second copy of the
 * parsing is how the two drifted the first time.
 *
 * Returns `{ name, address }`. `name` is never empty when there is anything
 * at all to show — it falls back to the address, so a row never renders a
 * blank sender. `address` is "" when the header carried only a name, or when
 * it is the same string as the name, so the caller can skip the muted span
 * rather than print the address twice.
 */
const ANGLE_ADDR = /^\s*(.*?)\s*<([^>]*)>\s*$/;

export const parseEmailFrom = (from) => {
  const raw = String(from ?? "").trim();
  if (!raw) return { name: "(unknown sender)", address: "" };

  const match = raw.match(ANGLE_ADDR);
  if (match) {
    /* Quoted display names are common: `"Doe, Jane" <jane@x.com>`. */
    const name = match[1].replace(/^["']|["']$/g, "").trim();
    const address = match[2].trim();
    if (!name) return { name: address || raw, address: "" };
    return { name, address: address === name ? "" : address };
  }

  /* A bare address, or a stray `<addr>` with no name. */
  const bare = raw.replace(/[<>]/g, "").trim();
  return { name: bare || raw, address: "" };
};

export default parseEmailFrom;
